import React, { useState, useEffect, useCallback } from 'react';
import { X, TrendingUp, Target, Trophy, ChevronRight, Lock, CheckCircle2, BarChart3, Clock, Minus, Plus } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import axios from 'axios';
import { API_URL } from '../config/api';

const POINTS_PER_GAME = 5;

const MovieStockGame = ({ user, isOpen, onClose }) => {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [myEntries, setMyEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('games');
  const [allocations, setAllocations] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);

  const fetchGames = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/movie-stock/games?user_id=${user.id}`);
      setGames(res.data.games || []);
    } catch (err) {
      console.error('Failed to fetch games:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchMyEntries = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API_URL}/api/movie-stock/my-entries?user_id=${user.id}`);
      setMyEntries(res.data);
    } catch (err) { console.error(err); }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      fetchGames();
      fetchMyEntries();
    }
  }, [isOpen, fetchGames, fetchMyEntries]);

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + v, 0);
  const remaining = POINTS_PER_GAME - totalAllocated;

  const adjustAllocation = (outcome, delta) => {
    setAllocations(prev => {
      const current = prev[outcome] || 0;
      const newVal = current + delta;
      const newTotal = totalAllocated + delta;
      if (newVal < 0 || newTotal > POINTS_PER_GAME) return prev;
      const updated = { ...prev };
      if (newVal === 0) {
        delete updated[outcome];
      } else {
        updated[outcome] = newVal;
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!selectedGame || totalAllocated < 1) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/movie-stock/submit`, {
        user_id: user.id,
        game_id: selectedGame.id,
        allocations,
      });
      await fetchGames();
      await fetchMyEntries();
      // Refresh game detail
      const res = await axios.get(`${API_URL}/api/movie-stock/games/${selectedGame.id}?user_id=${user.id}`);
      setSelectedGame(res.data);
      setAllocations({});
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const openGameDetail = async (game) => {
    try {
      const res = await axios.get(`${API_URL}/api/movie-stock/games/${game.id}?user_id=${user?.id || ''}`);
      setSelectedGame(res.data);
      setAllocations({});
      setLeaderboard(null);
      setView('detail');
    } catch (err) { console.error(err); }
  };

  const fetchGameLeaderboard = async (gameId) => {
    try {
      const res = await axios.get(`${API_URL}/api/movie-stock/games/${gameId}/leaderboard`);
      setLeaderboard(res.data);
    } catch (err) { console.error(err); }
  };

  // Countdown helper
  const getCountdown = (expiry) => {
    if (!expiry) return null;
    const diff = new Date(expiry).getTime() - Date.now();
    if (diff <= 0) return { text: 'Ended', color: 'text-red-400' };
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    if (d > 0) return { text: `${d}d ${h}h left`, color: d <= 2 ? 'text-amber-400' : 'text-emerald-400' };
    return { text: `${h}h left`, color: 'text-amber-400' };
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-lg bg-zinc-900 border-zinc-700 text-white p-0 max-h-[85dvh] overflow-y-auto rounded-xl" data-testid="movie-xchange-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b border-zinc-700 sticky top-0 bg-zinc-900 z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-bold" data-testid="movie-xchange-title">Movie Xchange</h2>
              <p className="text-[9px] sm:text-[10px] text-gray-500">5 points per game &middot; Predict &amp; Win</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 sm:p-1.5 rounded-lg hover:bg-zinc-700 text-gray-400" data-testid="movie-xchange-close">
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Nav tabs */}
        <div className="flex border-b border-zinc-700">
          {[
            { key: 'games', label: 'Games', icon: BarChart3 },
            { key: 'entries', label: 'My Entries', icon: Target },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setView(t.key); if (t.key === 'entries') fetchMyEntries(); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                (view === t.key || (view === 'detail' && t.key === 'games') || (view === 'leaderboard' && t.key === 'games'))
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
              data-testid={`mxchange-tab-${t.key}`}
            >
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>

        <div className="p-2.5 sm:p-3">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin h-7 w-7 border-2 border-emerald-500 border-t-transparent rounded-full" />
            </div>
          ) : view === 'games' ? (
            /* ==================== GAMES LIST ==================== */
            <div className="space-y-2" data-testid="xchange-games-list">
              {games.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No active games right now</p>
                  <p className="text-[10px] mt-1">Check back soon for new predictions!</p>
                </div>
              ) : games.map(game => {
                const countdown = getCountdown(game.expiry);
                return (
                  <button
                    key={game.id}
                    onClick={() => openGameDetail(game)}
                    className="w-full text-left bg-zinc-800 rounded-xl border border-zinc-700 p-3 hover:border-emerald-700/50 transition group"
                    data-testid={`game-card-${game.id}`}
                  >
                    <div className="flex gap-3">
                      {game.image && (
                        <img src={game.image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate pr-6">{game.title}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-400">
                          <span>{game.outcomes?.length} outcomes</span>
                          <span>{game.total_entries} players</span>
                          {countdown && (
                            <span className={`flex items-center gap-0.5 ${countdown.color}`}>
                              <Clock className="h-2.5 w-2.5" />{countdown.text}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-600 group-hover:text-emerald-400 self-center flex-shrink-0 transition" />
                    </div>
                    {game.user_entry && (
                      <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-900/20 border border-emerald-800/30">
                        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                        <span className="text-[10px] text-emerald-400 font-medium">
                          Submitted ({game.user_entry.total_points} pts allocated)
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

          ) : view === 'detail' && selectedGame ? (
            /* ==================== GAME DETAIL ==================== */
            <div className="space-y-4" data-testid="xchange-game-detail">
              <button onClick={() => setView('games')} className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
                <ChevronRight className="h-3 w-3 rotate-180" />Back to Games
              </button>

              {/* Game header */}
              <div className="flex gap-2.5 sm:gap-3">
                {selectedGame.image && (
                  <img src={selectedGame.image} alt="" className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs sm:text-sm font-bold text-white">{selectedGame.title}</h3>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                    <span>{selectedGame.total_entries} players</span>
                    {(() => {
                      const cd = getCountdown(selectedGame.expiry);
                      return cd ? <span className={`flex items-center gap-0.5 ${cd.color}`}><Clock className="h-2.5 w-2.5" />{cd.text}</span> : null;
                    })()}
                  </div>
                </div>
              </div>

              {/* Already submitted */}
              {selectedGame.user_entry ? (
                <div className="space-y-3">
                  <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4 space-y-3" data-testid="user-entry-summary">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-amber-400" />
                      <span className="text-sm font-semibold text-white">Your Allocation (Locked)</span>
                    </div>
                    <div className="space-y-1.5">
                      {Object.entries(selectedGame.user_entry.allocations).map(([outcome, pts]) => (
                        <div key={outcome} className="flex items-center justify-between px-3 py-2 bg-zinc-900 rounded-lg">
                          <span className="text-xs text-white font-medium">{outcome}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(pts / POINTS_PER_GAME) * 100}%` }} />
                            </div>
                            <span className="text-xs font-bold text-emerald-400 w-8 text-right">{pts} pts</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-500 text-center">
                      Total: {selectedGame.user_entry.total_points} / {POINTS_PER_GAME} points used
                    </p>
                  </div>

                  {/* Result if resolved */}
                  {selectedGame.status === 'resolved' && selectedGame.user_entry.final_score !== null && (
                    <div className={`p-4 rounded-xl border ${
                      selectedGame.user_entry.final_score > 0
                        ? 'bg-emerald-900/20 border-emerald-700/40'
                        : 'bg-red-900/20 border-red-700/40'
                    }`} data-testid="entry-result">
                      <div className="text-center space-y-1">
                        <Trophy className={`h-8 w-8 mx-auto ${selectedGame.user_entry.final_score > 0 ? 'text-amber-400' : 'text-gray-600'}`} />
                        <p className="text-xs text-gray-400">Winner: <span className="text-white font-semibold">{selectedGame.winning_outcome}</span></p>
                        <p className={`text-2xl font-bold ${selectedGame.user_entry.final_score > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {selectedGame.user_entry.final_score > 0 ? `+${selectedGame.user_entry.final_score}` : '0'} pts
                        </p>
                        {selectedGame.user_entry.winning_allocation > 0 && (
                          <p className="text-[10px] text-gray-500">
                            {selectedGame.user_entry.winning_allocation} pts on winner x2 = {selectedGame.user_entry.final_score}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Leaderboard button */}
                  {selectedGame.status === 'resolved' && (
                    <Button
                      onClick={() => { fetchGameLeaderboard(selectedGame.id); setView('leaderboard'); }}
                      variant="outline"
                      className="w-full border-zinc-700 text-gray-300 hover:text-white gap-2"
                      data-testid="view-leaderboard-btn"
                    >
                      <Trophy className="h-4 w-4 text-amber-400" />View Leaderboard
                    </Button>
                  )}
                </div>

              ) : selectedGame.status === 'active' ? (
                /* ==================== ALLOCATION FORM ==================== */
                <div className="space-y-3" data-testid="allocation-form">
                  {/* Points budget */}
                  <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-2.5 sm:p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] sm:text-xs font-semibold text-gray-300">Your Budget</p>
                      <div className="flex items-center gap-1">
                        <span className="text-base sm:text-lg font-bold text-emerald-400">{remaining}</span>
                        <span className="text-[10px] sm:text-xs text-gray-500">/ {POINTS_PER_GAME} pts left</span>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-zinc-700 rounded-full mt-2 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all"
                        style={{ width: `${(totalAllocated / POINTS_PER_GAME) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Outcome allocation cards — sorted alphabetically */}
                  <div className="bg-emerald-900/20 border border-emerald-800/30 rounded-lg px-3 py-2">
                    <p className="text-[11px] sm:text-xs text-emerald-300 leading-relaxed">
                      Distribute your <span className="font-bold">{POINTS_PER_GAME} points</span> across the outcomes below. Use the + / - buttons to allocate. Points on the winning outcome are doubled!
                    </p>
                  </div>
                  <p className="text-xs font-semibold text-gray-300">Allocate Points</p>
                  <div className="space-y-2">
                    {selectedGame.outcomes
                      .map((outcome, idx) => ({ outcome, idx }))
                      .sort((a, b) => a.outcome.localeCompare(b.outcome))
                      .map(({ outcome, idx }) => {
                      const pts = allocations[outcome] || 0;
                      const img = selectedGame.outcome_images?.[idx];
                      const price = selectedGame.outcome_prices?.[idx];
                      const pct = price ? (price * 100) : null;
                      return (
                        <div
                          key={outcome}
                          className={`bg-zinc-800 rounded-xl border p-2.5 sm:p-3 transition ${
                            pts > 0 ? 'border-emerald-700/50 bg-emerald-900/10' : 'border-zinc-700'
                          }`}
                          data-testid={`allocation-card-${idx}`}
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            {img && <img src={img} alt="" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs sm:text-sm font-semibold text-white truncate">{outcome}</p>
                              {pct !== null && (
                                <p className="text-[9px] sm:text-[10px] text-gray-500">{pct < 1 && pct > 0 ? '<1' : Math.round(pct)}% odds</p>
                              )}
                            </div>
                            {/* Point stepper */}
                            <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                              <button
                                onClick={() => adjustAllocation(outcome, -1)}
                                disabled={pts === 0}
                                className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-zinc-700 hover:bg-zinc-600 disabled:opacity-30 flex items-center justify-center transition"
                                data-testid={`alloc-minus-${idx}`}
                              >
                                <Minus className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" />
                              </button>
                              <span className={`text-sm sm:text-base font-bold w-5 sm:w-6 text-center ${pts > 0 ? 'text-emerald-400' : 'text-gray-600'}`}>
                                {pts}
                              </span>
                              <button
                                onClick={() => adjustAllocation(outcome, 1)}
                                disabled={remaining === 0}
                                className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-zinc-700 hover:bg-zinc-600 disabled:opacity-30 flex items-center justify-center transition"
                                data-testid={`alloc-plus-${idx}`}
                              >
                                <Plus className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" />
                              </button>
                            </div>
                          </div>
                          {pts > 0 && (
                            <div className="mt-1.5 sm:mt-2 flex items-center justify-between text-[9px] sm:text-[10px]">
                              <span className="text-gray-500">If wins: {pts} x 2 = <span className="text-emerald-400 font-bold">{pts * 2} pts</span></span>
                              <span className="text-gray-600">{Math.round((pts / POINTS_PER_GAME) * 100)}% of budget</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Summary & Submit */}
                  {totalAllocated > 0 && (
                    <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-3 space-y-2" data-testid="allocation-summary">
                      <p className="text-xs font-semibold text-gray-300">Summary</p>
                      <div className="space-y-1">
                        {Object.entries(allocations).map(([o, p]) => (
                          <div key={o} className="flex justify-between text-xs">
                            <span className="text-gray-400">{o}</span>
                            <span className="text-white font-medium">{p} pts {p > 0 && <span className="text-emerald-400">(win = {p * 2})</span>}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-xs border-t border-zinc-700 pt-1.5 mt-1.5">
                          <span className="text-gray-400">Total Allocated</span>
                          <span className="text-white font-bold">{totalAllocated} / {POINTS_PER_GAME}</span>
                        </div>
                      </div>

                      <Button
                        onClick={handleSubmit}
                        disabled={submitting || totalAllocated < 1}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-2"
                        data-testid="submit-allocation-btn"
                      >
                        {submitting ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4" />
                        )}
                        {submitting ? 'Submitting...' : `Lock In ${totalAllocated} Points`}
                      </Button>
                      <p className="text-[9px] text-gray-600 text-center">Once submitted, your allocation cannot be changed</p>
                    </div>
                  )}
                </div>

              ) : (
                /* Game closed or resolved without user entry */
                <div className="text-center py-6 text-gray-500 text-sm space-y-2">
                  {selectedGame.status === 'resolved' ? (
                    <>
                      <Trophy className="h-8 w-8 mx-auto text-amber-500" />
                      <p>Game Resolved</p>
                      <p className="text-emerald-400 font-bold text-lg">Winner: {selectedGame.winning_outcome}</p>
                      <Button
                        onClick={() => { fetchGameLeaderboard(selectedGame.id); setView('leaderboard'); }}
                        variant="outline"
                        className="border-zinc-700 text-gray-300 hover:text-white gap-2 mt-2"
                        data-testid="view-leaderboard-btn-2"
                      >
                        <Trophy className="h-4 w-4 text-amber-400" />View Leaderboard
                      </Button>
                    </>
                  ) : (
                    <>
                      <Lock className="h-8 w-8 mx-auto text-gray-600" />
                      <p>Submissions closed</p>
                    </>
                  )}
                </div>
              )}

              {/* Description / Rules */}
              {selectedGame.description && (
                <details className="bg-zinc-800 rounded-xl border border-zinc-700">
                  <summary className="px-3 py-2 text-xs font-semibold text-gray-400 cursor-pointer hover:text-white">Rules</summary>
                  <p className="px-3 pb-3 text-[11px] text-gray-500 leading-relaxed whitespace-pre-line">{selectedGame.description}</p>
                </details>
              )}
            </div>

          ) : view === 'leaderboard' && leaderboard ? (
            /* ==================== PER-GAME LEADERBOARD ==================== */
            <div className="space-y-2 sm:space-y-3" data-testid="xchange-leaderboard">
              <button onClick={() => setView('detail')} className="text-[11px] sm:text-xs text-gray-400 hover:text-white flex items-center gap-1">
                <ChevronRight className="h-3 w-3 rotate-180" />Back to Game
              </button>
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400" />
                <div>
                  <h3 className="text-xs sm:text-sm font-bold text-white">Leaderboard</h3>
                  <p className="text-[9px] sm:text-[10px] text-gray-500">{leaderboard.game_title}</p>
                </div>
              </div>
              {leaderboard.winning_outcome && (
                <div className="bg-emerald-900/20 border border-emerald-800/30 rounded-lg px-3 py-2 text-center">
                  <p className="text-[10px] text-gray-400">Winning Outcome</p>
                  <p className="text-sm font-bold text-emerald-400">{leaderboard.winning_outcome}</p>
                </div>
              )}
              {leaderboard.leaderboard.length === 0 ? (
                <p className="text-center text-gray-500 text-xs sm:text-sm py-6">No entries yet</p>
              ) : leaderboard.leaderboard.map((entry, i) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-xl ${
                    i === 0 ? 'bg-amber-900/20 border border-amber-800/30' :
                    i === 1 ? 'bg-zinc-800/80 border border-zinc-700' :
                    i === 2 ? 'bg-orange-900/10 border border-orange-800/20' :
                    'bg-zinc-800'
                  }`}
                  data-testid={`lb-rank-${i}`}
                >
                  <span className={`text-xs sm:text-sm font-bold w-5 sm:w-6 text-center ${
                    i === 0 ? 'text-amber-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-orange-400' : 'text-gray-600'
                  }`}>
                    {entry.rank}
                  </span>
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-zinc-700 flex items-center justify-center text-white text-[10px] sm:text-xs font-semibold overflow-hidden flex-shrink-0">
                    {entry.profile_picture
                      ? <img src={entry.profile_picture} alt="" className="w-full h-full object-cover" />
                      : entry.username?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] sm:text-xs text-white font-medium truncate">{entry.username}</p>
                    <p className="text-[9px] sm:text-[10px] text-gray-500">
                      {entry.winning_allocation != null ? `${entry.winning_allocation} pts on winner` : `${entry.total_points} pts allocated`}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm sm:text-base font-bold ${
                      entry.final_score > 0 ? 'text-emerald-400' : entry.final_score != null ? 'text-gray-600' : 'text-gray-500'
                    }`}>
                      {entry.final_score != null ? entry.final_score : '-'}
                    </p>
                    <p className="text-[8px] sm:text-[9px] text-gray-500">score</p>
                  </div>
                </div>
              ))}
            </div>

          ) : view === 'entries' ? (
            /* ==================== MY ENTRIES ==================== */
            <div className="space-y-3" data-testid="my-entries">
              {myEntries?.stats && (
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div className="bg-zinc-800 rounded-lg p-2.5 text-center">
                    <p className="text-sm font-bold text-white">{myEntries.stats.total_games}</p>
                    <p className="text-[9px] text-gray-500">Games</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-2.5 text-center">
                    <p className="text-sm font-bold text-emerald-400">{myEntries.stats.wins}</p>
                    <p className="text-[9px] text-gray-500">Wins</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-2.5 text-center">
                    <p className="text-sm font-bold text-amber-400">{myEntries.stats.total_score}</p>
                    <p className="text-[9px] text-gray-500">Total Score</p>
                  </div>
                </div>
              )}
              {(!myEntries?.entries || myEntries.entries.length === 0) ? (
                <div className="text-center py-8 text-gray-500">
                  <Target className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No entries yet. Join a game!</p>
                </div>
              ) : myEntries.entries.map(entry => (
                <div key={entry.id} className="bg-zinc-800 rounded-xl border border-zinc-700 p-3" data-testid={`entry-${entry.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-white truncate">{entry.game?.title || 'Unknown Game'}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {Object.entries(entry.allocations).map(([o, p]) => (
                          <span key={o} className="px-1.5 py-0.5 rounded bg-zinc-700 text-[10px] text-gray-300">
                            {o}: {p}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      {entry.final_score != null ? (
                        <span className={`text-sm font-bold ${entry.final_score > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {entry.final_score > 0 ? `+${entry.final_score}` : '0'}
                        </span>
                      ) : (
                        <span className="text-[10px] text-amber-400 font-medium flex items-center gap-0.5">
                          <Lock className="h-3 w-3" />Pending
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MovieStockGame;
